/**
 * groups.js — Cognito group constants and helpers for Project 217
 *
 * Single source of truth for group names, route access, labels, and colors.
 * Import from here instead of redefining in each component.
 *
 * Cognito group casing rules:
 *   MEMBERSHIP  — uppercase (existing Cognito group)
 *   superadmin  — lowercase
 *   admin       — lowercase (when added)
 *   staff       — lowercase (when added)
 *   supervisors — lowercase (when added)
 *   hr          — lowercase (when added)
 *   students    — lowercase (when added)
 *
 * Usage:
 *   import { ROUTE_GROUPS, GROUP_LABELS, isAdminOrAbove } from "../utils/groups";
 */

// ── Route access — single source of truth ─────────────────────────────────────
// Both ProtectedRoute (frontend) and Lambda group checks must match these.
export const ROUTE_GROUPS = {
  '/fulfillment':        ['MEMBERSHIP', 'admin', 'superadmin'],
  '/promotions':         ['MEMBERSHIP', 'admin', 'superadmin'],
  '/dashboard':          ['MEMBERSHIP', 'admin', 'superadmin'],
  '/events/id-matching': ['staff', 'MEMBERSHIP', 'admin', 'superadmin', 'supervisors', 'hr', 'students'],
  '/events/purls':       ['staff', 'MEMBERSHIP', 'admin', 'superadmin', 'supervisors', 'hr', 'students'],
  '/membership':         ['MEMBERSHIP', 'admin', 'superadmin'],
  '/aisrequests':        ['MEMBERSHIP', 'admin', 'superadmin'],
  '/admin':              ['admin', 'superadmin'],
  '/admin/templates':    ['superadmin'],
  '/time':               ['supervisors', 'hr', 'students', 'admin', 'superadmin'],
  '/passes':             ['MEMBERSHIP', 'admin', 'superadmin'],
};

// ── Group metadata ─────────────────────────────────────────────────────────────
export const GROUP_LABELS = {
  superadmin:  'Super Admin',
  admin:       'Admin',
  MEMBERSHIP:  'Membership Staff',
  staff:       'Staff',
  supervisors: 'Supervisor',
  hr:          'HR',
  students:    'Student Worker',
};

export const GROUP_COLORS = {
  superadmin:  'bg-red-50 text-red-700 border-red-200',
  admin:       'bg-purple-50 text-purple-700 border-purple-200',
  MEMBERSHIP:  'bg-blue-50 text-blue-700 border-blue-200',
  staff:       'bg-slate-100 text-slate-600 border-slate-200',
  supervisors: 'bg-amber-50 text-amber-700 border-amber-200',
  hr:          'bg-green-50 text-green-700 border-green-200',
  students:    'bg-teal-50 text-teal-700 border-teal-200',
};

// ── Assignable groups ──────────────────────────────────────────────────────────
/** Groups that admin-level users can assign (excludes superadmin) */
export const ADMIN_ASSIGNABLE_GROUPS = [
  'staff', 'students', 'supervisors', 'hr', 'MEMBERSHIP',
];

/** All groups in the system */
export const ALL_GROUPS = [
  ...ADMIN_ASSIGNABLE_GROUPS,
  'admin',
  'superadmin',
];

// ── Helper functions ───────────────────────────────────────────────────────────
/** Returns true if the caller belongs to superadmin. */
export function isSuperAdmin(groups) {
  return Array.isArray(groups)
    ? groups.includes('superadmin')
    : groups.has('superadmin');
}

/** Returns true if the caller belongs to admin or superadmin. */
export function isAdminOrAbove(groups) {
  const check = Array.isArray(groups) ? (g) => groups.includes(g) : (g) => groups.has(g);
  return check('admin') || check('superadmin');
}

/** Returns true if the caller has access to fulfillment and promotions features. */
export function isMembershipOrAbove(groups) {
  const check = Array.isArray(groups) ? (g) => groups.includes(g) : (g) => groups.has(g);
  return check('MEMBERSHIP') || check('admin') || check('superadmin');
}